import { useState } from 'react';
import {
  useInterviewState,
  useStartOverInterview,
  useSetInterviewAnswer,
} from '../hooks/useBuildGameInterview';
import { GameDesignSummary } from './GameDesignSummary';
import type {
  BuildGameQuestion,
  BuildGameInterviewState,
} from '@volley/crucible-ipc-types/build-game-types';

/**
 * Review-and-edit modal — opened from the chat panel's
 * `<ReviewAnswersButton>`. Renders the full `<GameDesignSummary>`
 * with Edit buttons; clicking Edit flips the modal into a per-
 * question edit panel where the user picks a new answer (single-
 * select buttons or free-text input depending on the question's
 * type) and saves.
 *
 * Saving dispatches `build-game:set-answer` via the
 * `useSetInterviewAnswer` hook (PR #1's IPC + outbox). The skill
 * (PR #4) drains the outbox on its next preflight and applies the
 * edit. The user sees the change reflected once the renderer's
 * React Query cache invalidates — handled by the hook.
 *
 * "Start over" archives the current state via `build-game:start-over`
 * and closes the modal.
 */

export function ReviewAnswersModal({
  gameId,
  onClose,
}: {
  gameId: string;
  onClose: () => void;
}): React.JSX.Element | null {
  const { data: state, isLoading } = useInterviewState(gameId);
  const { startOver } = useStartOverInterview();
  const { setAnswer } = useSetInterviewAnswer();
  const [confirmStartOver, setConfirmStartOver] = useState(false);
  // When non-null, the modal's body switches from the summary view
  // to the per-question edit panel.
  const [editing, setEditing] = useState<{ agent: string; questionId: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  if (!gameId) return null;

  const handleStartOver = async (): Promise<void> => {
    const result = await startOver(gameId);
    if (result.ok) {
      onClose();
    }
  };

  const beginEdit = (args: { agent: string; questionId: string }): void => {
    if (!state) return;
    // Crucible #570: `state.agents` is now optional — SKILL-written
    // quick-batch plan-mode state has no engine fields. The
    // review-and-edit modal only mounts after Phase A engine state
    // exists, but guard the read defensively in case it's invoked
    // mid-transition.
    //
    // Crucible #936: `agents[agent].questions` can also be an
    // object-keyed-by-id mid-bootstrap (hw#206 path). `.find` on a
    // non-array throws and the modal crashes catastrophically.
    // Match the main-side guard at `build-game-state.ts:231` —
    // treat any non-array shape as "no editable question record yet"
    // and bail out cleanly. The next state write normalises the
    // shape back to an array and the user can retry the click.
    const rawQuestions = state.agents?.[args.agent]?.questions;
    if (!Array.isArray(rawQuestions)) return;
    const question = rawQuestions.find((q) => q.id === args.questionId);
    if (!question) return;
    setEditValue(question.answer ?? '');
    setEditing(args);
  };

  const cancelEdit = (): void => {
    setEditing(null);
    setEditValue('');
  };

  const saveEdit = async (): Promise<void> => {
    if (!editing || !editValue.trim()) return;
    setSaving(true);
    try {
      await setAnswer({
        gameId,
        agent: editing.agent,
        questionId: editing.questionId,
        answer: editValue.trim(),
      });
      setEditing(null);
      setEditValue('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-answers-title"
      data-testid="review-answers-modal"
      // Codex review #4 MEDIUM (PR #646 / #643) — bumped from z-50
      // to z-[60] so this review modal beats the preview-recovery
      // toast (z-[55]). See DependencyApprovalModal for the full
      // rationale.
      //
      // Issue #965 — `app-region-no-drag` defence-in-depth. The
      // Workspace's page-level `<header>` underneath carries
      // `-webkit-app-region: drag` via `app-region-drag` (see #639 +
      // #900). Drag-region hit-testing is OS-compositor level, so
      // without this carve-out the underlying header drag region
      // bleeds through and intercepts clicks at the top strip of the
      // modal — backdrop click-to-dismiss and any control near the
      // top edge silently fail. Mirrors the PreviewOverlay (#847)
      // and the UpdateTemplateModal / CreateGameDialog (#900)
      // carve-outs.
      className="app-region-no-drag fixed inset-0 z-[60] flex items-start justify-center overflow-auto bg-black/70 p-8"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-crucible-surface p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h2 id="review-answers-title" className="text-lg font-semibold">
              {editing ? 'Edit answer' : 'Review your interview answers'}
            </h2>
            <p className="mt-1 text-sm text-white/50">
              {editing
                ? "Pick a new answer; we'll route it through the agent."
                : 'Click Edit next to any answer to revise it. Edits apply when the agent next picks up; they survive a Foundry crash if it happens between now and then.'}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-white/40 hover:text-white/80"
          >
            ✕
          </button>
        </header>

        {isLoading && <p className="text-sm text-white/40">Loading…</p>}
        {!isLoading && !state && (
          <p className="text-sm text-white/40">
            No interview in progress for this game. Start one from the Build panel with{' '}
            <code>/build-game</code>.
          </p>
        )}

        {state && !editing && <GameDesignSummary state={state} onEdit={beginEdit} />}

        {state && editing && (
          <EditPanel
            state={state}
            target={editing}
            value={editValue}
            onChange={setEditValue}
            onSave={() => void saveEdit()}
            onCancel={cancelEdit}
            saving={saving}
          />
        )}

        {!editing && (
          <footer className="mt-6 flex items-center justify-between gap-2 border-t border-white/10 pt-4">
            {!confirmStartOver ? (
              <button
                onClick={() => setConfirmStartOver(true)}
                className="text-xs text-red-300/80 hover:text-red-200"
              >
                Start over from scratch
              </button>
            ) : (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-white/60">Archive these answers and start fresh?</span>
                <button
                  onClick={() => void handleStartOver()}
                  className="rounded bg-red-500/80 px-2 py-0.5 text-white hover:bg-red-500"
                >
                  Yes, archive
                </button>
                <button
                  onClick={() => setConfirmStartOver(false)}
                  className="rounded border border-white/10 px-2 py-0.5 text-white/60 hover:bg-white/5"
                >
                  Cancel
                </button>
              </div>
            )}
            <button
              onClick={onClose}
              className="rounded-lg bg-white/10 px-4 py-2 text-sm text-white hover:bg-white/15"
            >
              Done
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

/**
 * Per-question edit panel rendered inside the modal when the user
 * clicks an Edit button. Single-select questions render the option
 * list as buttons; free-text questions render a textarea with the
 * existing answer pre-filled.
 */
function EditPanel({
  state,
  target,
  value,
  onChange,
  onSave,
  onCancel,
  saving,
}: {
  state: BuildGameInterviewState;
  target: { agent: string; questionId: string };
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}): React.JSX.Element {
  // Crucible #570: `state.agents` is optional now — guard the read.
  // Crucible #936 (Codex round-2): the bootstrap can land
  // object-keyed `questions` mid-transition; `.find` on a non-array
  // throws and re-trips the `H.find/H.map` class of crashes the
  // ChatErrorBoundary would otherwise catch. Match the `beginEdit`
  // guard above — treat any non-array as "not found yet" and let
  // the user retry once the state normalises.
  const rawQuestions = state.agents?.[target.agent]?.questions;
  const question: BuildGameQuestion | undefined = Array.isArray(rawQuestions)
    ? rawQuestions.find((q) => q.id === target.questionId)
    : undefined;
  if (!question) {
    return (
      <p className="text-sm text-red-300">
        Couldn't find that question — the interview state may have changed since you opened the
        modal. Close and try again.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-xs uppercase tracking-wide text-white/40">{target.agent}</p>
        <p className="mt-1 text-sm text-white/80">{question.text}</p>
      </div>

      {question.type === 'single-select' && Array.isArray(question.options) && (
        <ul className="flex flex-col gap-2">
          {question.options.map((opt) => (
            <li key={opt}>
              <button
                onClick={() => onChange(opt)}
                className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                  value === opt
                    ? 'border-crucible-accent bg-crucible-accent/20 text-white'
                    : 'border-white/10 bg-white/5 text-white/80 hover:bg-white/10'
                }`}
              >
                {opt}
              </button>
            </li>
          ))}
        </ul>
      )}

      {question.type === 'free-text' && (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={question.rendererHint === 'long' ? 4 : 2}
          placeholder={question.example}
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm focus:border-crucible-accent focus:outline-none"
          autoFocus
        />
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={saving}
          className="rounded border border-white/10 px-3 py-1.5 text-sm text-white/70 hover:bg-white/5 disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={saving || !value.trim() || value.trim() === question.answer}
          data-testid="review-answers-save-edit"
          className="rounded-lg bg-crucible-accent px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save edit'}
        </button>
      </div>
    </div>
  );
}
